<template lang="html">
  <div v-if="school">
    <h1 v-if="schoolTitle">{{ school.name }}</h1>

    <!-- Dynamically render the fields to edit -->
    <div v-for="field in editable_fields" class="">
      <pl-input v-model="school[field]" :labelText="`Edit: ${field}`" />
    </div>

    <!-- Save settings button -->
    <div>
      <pl-button @click.native="saveSettings" class="pl-primary">
        Save Settings
      </pl-button>
    </div>

    <br />

    <!-- Dynamically render non editable_fields -->
    <div v-for="field in non_editable_fields" class="">
      <span class="pl-subheading"
        ><strong>{{ field }}</strong> : {{ school[field] }}</span
      >
    </div>

    <br />

    <!-- Edit Invite code Section -->
    <div class="pl-display-1">Invite code</div>

    <!-- If school alrady has an invite -->
    <template v-if="school.invites.length">
      <!-- Edit School Code -->
      <div class="pl-subheading">
        This is the code that admins can share with teachers so teachers can
        join parlay under the school
        <br />
        You can edit here to make it more customized. The code must be between
        4-10 characters. The code cannot be the same as the code for any other
        schools.
        <pl-input
          v-model="school.invites[0].code"
          placeholder="Edit the code here"
        />
      </div>

      <!-- Save Code button -->
      <div>
        <pl-button @click.native="editInviteCode" class="pl-primary">
          Save Code
        </pl-button>
      </div>

      <div class="mt4 pl-display-1">Email Suffix</div>
      <!-- Edit School Email suffix -->
      <div class="pl-subheading">
        Here you can edit the schools email suffix. This means that whenever a
        teacher tries to updgrade their account with a school code, they will
        only be allowed to do so if their email contains the suffix you've
        specified. If you don't specify anything, anyone can use the code
        <multiselect
          v-model="selected_email_suffixes"
          placeholder="All Emails Suffixes"
          tag-placeholder="Add this as Email Suffix"
          :options="email_suffixes"
          :multiple="true"
          :taggable="true"
          @tag="addEmailSuffix"
          @remove="deleteEmailSuffix"
          style="max-width: 100%; height: 100%"
        >
        </multiselect>
      </div>

      <!-- Save Email Suffix button -->
      <div>
        <pl-button @click.native="editEmailSuffix" class="pl-primary">
          Save Email Suffix
        </pl-button>
      </div>

      <!-- Link that teachers can use to sign up under school -->
      <div class="mt4 pl-display-1">Invite Link</div>
      <div class="pl-subheading">
        Teachers can use this link to sign up directly under a school the moment
        they register for parlay.
        <br />
        NOTE: If you changed the code, make sure to save it before copying and
        sharing the link below.
        <br />
        Link: <strong>{{ sharable_signup_link }}</strong>
      </div>

      <div class="mt4 pl-display-1">Ed.Link Integrations</div>
      <toggle-button
        :sync="true"
        :labels="true"
        :value="edlink_integration_enabled"
        @change="toggleEdLinkConnection($event)"
        class="mt2 ml2"
        color="#72ccd7"
      />
      <div class="pl-display-1 mt3">Admin Portal Link</div>
      <div class="pl-subheading">
        <div>Teachers can use this link directly to the admin portal.</div>
        Link:
        <strong>{{ admin_portal_link }}</strong>
      </div>
    </template>

    <!-- If we are creating an invite code for the school for the first time -->
    <template v-else>
      <div class="pl-subheading">
        This school has no invite code as of yet. Create one below....
      </div>
      <pl-input
        v-model="new_invite_code"
        placeholder="Insert the new code here"
      />

      <!-- Save Code button -->
      <div>
        <pl-button @click.native="createInviteCode" class="pl-primary">
          Create Code
        </pl-button>
      </div>
    </template>
  </div>
</template>

<script>
  import adminServices from 'services/admin/adminServices';
  import { EventBus } from 'utils/event-bus';
  import urlConfigs from 'services/apiConfigs';
  import Multiselect from 'vue-multiselect';

  export default {
    components: {
      Multiselect,
    },
    data() {
      return {
        new_invite_code: '',
        school: null,
        editable_fields: [
          'name',
          'street_address',
          'city',
          'country',
          'phone_number',
          'website',
          'thumbnail_url',
        ],
        non_editable_fields: ['id', 'created_at'],
        selected_email_suffixes: [],
        email_suffixes: [],
        edlink_integration_enabled: true,
      };
    },
    props: {
      schoolTitle: {
        type: Boolean,
        default() {
          return true;
        },
      },
    },
    computed: {
      sharable_signup_link() {
        const formatted_invite_code = this.formatParam(
          this.school.invites[0].code
        );
        return (
          urlConfigs.uiURL +
          '/account/signup?school_invite_code=' +
          formatted_invite_code
        );
      },
      admin_portal_link() {
        return urlConfigs.uiURL + '/admin-portal/' + this.school.id + '/users';
      },
    },
    methods: {
      formatParam(param) {
        // Replace special characters with correct encoding to safely read URL
        return (
          param
            // /g performs a global replace on the string insted of just the first_name
            // character that matches
            .replace(/%/g, '%25')
            .replace(/ /g, '%20')
            .replace(/\$/g, '%24')
            .replace(/#/g, '%23')
        );
      },
      editInviteCode() {
        const invite_id = this.school.invites[0].id;
        const code = this.school.invites[0].code;
        adminServices
          .editSchoolInvite(invite_id, { code })
          .then(({ data }) => {
            const { code } = data;
            this.alertToast('Success')();
            this.$store.dispatch('updateCurrentSchoolInvite', { code });
          })
          .catch(this.alertToast('Failure'));
      },
      editEmailSuffix() {
        const invite_id = this.school.invites[0].id;
        adminServices
          .editSchoolInvite(invite_id, {
            email_suffixes: this.selected_email_suffixes,
          })
          .then(({ data }) => {
            this.alertToast('Success')();
            const { email_suffixes } = data;
            this.$store.dispatch('updateCurrentSchoolInvite', {
              email_suffixes,
            });
          })
          .catch(this.alertToast('Failure'));
      },
      createInviteCode() {
        adminServices
          .createSchoolCode(this.school.id, this.new_invite_code)
          .then((res) => {
            this.alertToast('Success')();
            this.school.invites.push(res.data);
          })
          .catch(this.alertToast('Failure'));
      },
      alertToast(msg) {
        return () => {
          EventBus.$emit('toast-msg', msg);
        };
      },
      saveSettings() {
        // Send payload with the fields that are editable
        const edit_payload = {};
        this.editable_fields.forEach((field) => {
          edit_payload[field] = this.school[field];
        });
        adminServices
          .editSchool(this.$route.params.schoolID, edit_payload)
          .then(({ data }) => {
            this.alertToast('Success')();
            this.$store.dispatch('updateSchoolDetails', data);
          })
          .catch(this.alertToast('Failure'));
      },
      addEmailSuffix(newTag) {
        if (!this.email_suffixes.includes(newTag)) {
          this.email_suffixes.push(newTag);
        }
        if (!this.selected_email_suffixes.includes(newTag)) {
          this.selected_email_suffixes.push(newTag);
        }
      },
      deleteEmailSuffix(tag) {
        const index_email_suffixes = this.email_suffixes.indexOf(tag);
        const index_selected_email_suffixes =
          this.selected_email_suffixes.indexOf(tag);
        if (this.email_suffixes.includes(tag)) {
          this.email_suffixes.splice(index_email_suffixes, 1);
        }
        if (this.selected_email_suffixes.includes(tag)) {
          this.selected_email_suffixes.splice(index_selected_email_suffixes, 1);
        }
      },
      toggleEdLinkConnection(e) {
        this.edlink_integration_enabled = e.value;
        const payload = {
          edlink_integration_is_enabled: e.value,
        };
        adminServices
          .editSchool(this.$route.params.schoolID, payload)
          .then(({ data }) => {
            this.$store.dispatch('updateSchoolDetails', data);
          });
      },
    },
    mounted() {
      this.$store
        .dispatch('getSchoolDetails', this.$route.params.schoolID)
        .then((data) => {
          this.school = data;
          if (this.school.invites.length) {
            this.selected_email_suffixes =
              this.school.invites[0].email_suffixes;
            this.email_suffixes = this.school.invites[0].email_suffixes;
          }
          this.edlink_integration_enabled =
            this.school.edlink_integration_is_enabled;
        });
    },
  };
</script>

<style lang="css"></style>



// WEBPACK FOOTER //
// editSchoolInfo.vue?c2f5c84c